import { assignValue, findMatch, hasKey } from '@/utils/resultsHelper';
import { findWithUUID } from '@/utils/uuidHelper';
import { cohortChart, cohortVisualisationStates } from '../constants';
import { findCohort } from '../../state/shared';

const axisMapping = (value = null, label = null) => ({
  value,
  label,
});

const baseCohortChart = (chart, selected) => ({
  code: chart,
  selected,
});

const barChart = (xAxis) => ({
  ...baseCohortChart(cohortChart.BAR, true),
  xAxis: xAxis ? axisMapping(xAxis.name, xAxis.label) : axisMapping(),
});

const stackedBarChart = (xAxis) => ({
  ...baseCohortChart(cohortChart.STACKED_BAR, false),
  xAxis: xAxis ? axisMapping(xAxis.name, xAxis.label) : axisMapping(),
});

const boxPlot = (xAxis, yAxis) => ({
  ...baseCohortChart(cohortChart.BOX_PLOT, false),
  xAxis: xAxis ? axisMapping(xAxis.name, xAxis.label) : axisMapping(),
  yAxis: yAxis ? axisMapping(yAxis.name, yAxis.label) : axisMapping(),
});

const histogram = (yAxis) => ({
  ...baseCohortChart(cohortChart.HISTOGRAM, true),
  yAxis: yAxis ? axisMapping(yAxis.name, yAxis.label) : axisMapping(),
  bins: { value: 10 },
});

export const cohortVisualisationState = () => [
  barChart(),
  boxPlot(),
  stackedBarChart(),
  histogram(),
];

const queryVisualisation = (visualisation) => {
  const queryVisualResult = {
    code: visualisation.code,
    statistics: [],
    distinctValues: [],
    values: [],
    status: null,
    xAxis: visualisation.xAxis,
    ...cohortVisualisationStates,
  };

  if ([cohortChart.BOX_PLOT, cohortChart.HISTOGRAM].includes(visualisation.code)) {
    queryVisualResult.yAxis = visualisation.yAxis;
  }

  if (visualisation.code === cohortChart.HISTOGRAM) {
    queryVisualResult.bins = visualisation.bins;
  }

  return queryVisualResult;
};

const variableMatches = ({ visualisations }, value, variableName) => {
  const matches = visualisations.filter((v) => hasKey(v, variableName));
  const values = matches.map((match) => match[variableName].value).filter((v, i, a) => a.indexOf(v) === i);

  return values.length === 1 && values[0] === value ? [] : matches;
};

export const resetVisualisations = ({ visualisations, queries }) => {
  const queryVisualisations = visualisations.map(queryVisualisation);
  queries.forEach((q) => assignValue(q, 'visualisations', queryVisualisations));
};

export const resetMatchingVisualisationsOnName = (cohort, { name, label }, variableName) => {
  const matches = variableMatches(cohort, name, variableName);
  if (matches.length > 0) {
    matches.forEach((vis) => {
      assignValue(vis[variableName], 'value', name);
      assignValue(vis[variableName], 'label', label);
    });
    resetVisualisations(cohort);
  }
};

export const resetMatchingVisualisationsOnValue = (cohort, value, variableName) => {
  const matches = variableMatches(cohort, value, variableName);
  if (matches.length > 0) {
    matches.forEach((vis) => { assignValue(vis[variableName], 'value', value); });
    resetVisualisations(cohort);
  }
};

export const selectVisualisation = ({ visualisations }, chartType) => {
  const visualisation = findMatch(visualisations, chartType);

  visualisations.forEach((chart) => { assignValue(chart, 'selected', false); });
  visualisation.selected = true;
};

export const findCohortVisualisation = (draft, action) => {
  const item = findCohort(draft, action);
  const query = (action.queryUUID && item.queries) ? findWithUUID(item.queries, action.queryUUID) : null;
  const visualisation = (query && query.visualisations) ? findMatch(query.visualisations, action.chartType) : null;

  return { item, query, visualisation };
};

export default queryVisualisation;
